import { Link, useSearchParams } from 'react-router-dom';
import type { Device } from '@iot-dashboard/shared';
import { CAMERA_KINDS, KIND_LABELS, emptyStatusCounts, isCameraKind } from '@iot-dashboard/shared';
import { useDevices, useTenantOverview } from '../../api/hooks';
import { KindIcon } from '../../components/KindIcon';
import { Panel } from '../../components/Panel';
import { SeverityBadge } from '../../components/SeverityBadge';
import { StatRow, StatTile } from '../../components/StatTile';
import { StatusDot } from '../../components/StatusDot';
import { StatusSummary } from '../../components/StatusSummary';
import { ErrorState, Loading } from '../../components/States';
import { TimeAgo } from '../../components/TimeAgo';
import { PageHeader } from '../../layout/PageHeader';
import { useTenantContext } from '../../layout/TenantLayout';
import { formatLux, formatPercent, formatTemperature } from '../../lib/format';

function primaryReading(d: Device): { label: string; value: string }[] {
  const m = d.metrics;
  switch (d.kind) {
    case 'environmental':
      return [
        { label: 'Temp', value: formatTemperature(m.temperature) },
        { label: 'Humidity', value: formatPercent(m.humidity) },
        { label: 'Light', value: formatLux(m.light) },
      ];
    case 'entry':
      return [{ label: 'Door', value: m.isOpened === undefined ? '—' : m.isOpened ? 'Open' : 'Closed' }];
    case 'motion':
      return [{ label: 'Motion', value: m.motionDetected === undefined ? '—' : m.motionDetected ? 'Detected' : 'Clear' }];
    default:
      return [{ label: 'Battery', value: formatPercent(m.batteryPercentage) }];
  }
}

export function OverviewPage() {
  const { tenantId, tenant, base } = useTenantContext();
  const [params] = useSearchParams();
  const siteId = params.get('site') ?? undefined;
  const overview = useTenantOverview(tenantId);
  const devices = useDevices(tenantId, siteId ? { siteId } : undefined);

  if (overview.isPending) return <Loading label="Loading overview…" />;
  if (overview.isError) return <ErrorState error={overview.error} retry={() => overview.refetch()} />;
  const o = overview.data;
  const list = devices.data ?? [];
  // byKind covers every device, so sensors and cameras are counted apart here.
  const cameraTotal = CAMERA_KINDS.reduce((n, k) => n + (o.counts.byKind[k] ?? 0), 0);
  const sensorTotal = Object.values(o.counts.byKind).reduce((a, b) => a + (b ?? 0), 0) - cameraTotal;
  const sensors = list.filter((d) => !isCameraKind(d.kind));
  const cameras = list.filter((d) => isCameraKind(d.kind));
  const counts = devices.data
    ? sensors.reduce((c, d) => ((c[d.status] += 1), c), emptyStatusCounts())
    : o.counts.devices;
  const site = siteId ? o.sites.find((s) => s.siteId === siteId) : undefined;
  const openAlerts = o.recentAlerts.filter((a) => !siteId || a.siteId === siteId);
  const suffix = siteId ? `?site=${encodeURIComponent(siteId)}` : '';
  const needsIntegration = !o.integration?.hasApiKey || !o.integration.enabled;

  return (
    <>
      <PageHeader
        title={site ? site.name : `${tenant.name} overview`}
        subtitle={
          site
            ? ['Console', site.ipAddress, site.location].filter(Boolean).join(' · ')
            : [
                `${o.counts.sites} site${o.counts.sites === 1 ? '' : 's'}`,
                `${sensorTotal} sensor${sensorTotal === 1 ? '' : 's'}`,
                ...(cameraTotal > 0 ? [`${cameraTotal} camera${cameraTotal === 1 ? '' : 's'}`] : []),
              ].join(' · ')
        }
        actions={o.integration?.lastSyncAt && !site ? <span className="small muted">Synced <TimeAgo iso={o.integration.lastSyncAt} /></span> : null}
      />
      {needsIntegration ? (
        <div className="alert-banner alert-banner--info">
          <div className="grow">
            <strong>Connect this tenant's consoles to start monitoring.</strong> Add its API key so the dashboard can read its sensors and cameras.
          </div>
          <Link to={`${base}/integrations`} className="btn btn--primary btn--sm">Set up integration</Link>
        </div>
      ) : o.integration?.lastSyncStatus === 'error' ? (
        <div className="alert-banner alert-banner--warning">
          <div className="grow"><strong>The last sync failed.</strong> {o.integration.lastSyncError}</div>
          <Link to={`${base}/integrations`} className="btn btn--sm">Check integration</Link>
        </div>
      ) : null}

      <Panel title="Sensor status" actions={<Link to={`${base}/sensors${suffix}`} className="small">View all sensors</Link>}>
        <StatusSummary counts={counts} />
      </Panel>

      <StatRow>
        <StatTile label="Sites" value={o.counts.sites} to={`${base}/sites`} tone="accent" />
        <StatTile label="Sensors" value={siteId ? sensors.length : sensorTotal} to={`${base}/sensors${suffix}`} />
        <StatTile label="Cameras" value={siteId ? cameras.length : cameraTotal} hint="cameras and doorbells" to={`${base}/cameras${suffix}`} />
        <StatTile label="Open alerts" value={openAlerts.length} to={`${base}/alerts`} tone={openAlerts.length ? 'critical' : 'good'} />
        <StatTile label="Environmental" value={sensors.filter((d) => d.kind === 'environmental').length} hint="temperature · humidity · leak" to={`${base}/sensors?kind=environmental`} />
        <StatTile label="Entry" value={sensors.filter((d) => d.kind === 'entry').length} hint="doors and windows" to={`${base}/sensors?kind=entry`} />
      </StatRow>

      <div className="grid-2">
        <Panel title="Open alerts" flush actions={<Link to={`${base}/alerts`} className="small">All alerts</Link>}>
          {openAlerts.length === 0 ? (
            <div className="empty">No open alerts</div>
          ) : (
            <ul className="list">
              {openAlerts.map((a) => (
                <li key={a.alertId} className="list__item">
                  <SeverityBadge severity={a.severity} />
                  <div className="grow">
                    <Link to={`${base}/sensors/${encodeURIComponent(a.deviceId)}`}>{a.message}</Link>
                    <div className="small muted">{a.ruleName} · opened <TimeAgo iso={a.openedAt} /></div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Recent events" flush actions={<Link to={`${base}/events`} className="small">Event log</Link>}>
          {o.recentEvents.length === 0 ? (
            <div className="empty">No events yet</div>
          ) : (
            <ul className="list">
              {o.recentEvents.map((e) => (
                <li key={e.eventId} className="list__item">
                  <span className="list__time"><TimeAgo iso={e.ts} /></span>
                  <div className="grow">
                    <div>{e.title}</div>
                    <div className="small muted">{e.type}{e.actor ? ` · ${e.actor}` : ''}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <Panel title="Current readings" actions={devices.isFetching ? <span className="small muted">Refreshing…</span> : null}>
        {devices.isPending ? (
          <Loading />
        ) : sensors.length === 0 ? (
          <div className="empty">No sensors have been synced for this {siteId ? 'site' : 'tenant'} yet.</div>
        ) : (
          <div className="sensor-cards">
            {sensors.map((d) => (
              <Link key={d.deviceId} to={`${base}/sensors/${encodeURIComponent(d.deviceId)}`} className="sensor-card">
                <div className="sensor-card__title">
                  <KindIcon kind={d.kind} />
                  <span className="grow">{d.name}</span>
                  <StatusDot status={d.status} label={false} />
                </div>
                <div className="sensor-card__metrics">
                  {primaryReading(d).map((r) => (
                    <div key={r.label} className="sensor-card__metric">
                      <b>{r.value}</b>
                      <span>{r.label}</span>
                    </div>
                  ))}
                </div>
                <div className="small muted" style={{ marginTop: 8 }}>{KIND_LABELS[d.kind]} · seen <TimeAgo iso={d.lastSeenAt} /></div>
              </Link>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}
